import { useLocation, useNavigate } from 'react-router'

import { useUnreadCount } from '@/features/notifications/api/queries'
import { useSupportChat } from '@/features/support/lib/useSupportChat'
import { toPersianDigits } from '@/shared/lib'

import { Icon } from './Icon'

/**
 * The top bar on the screens that carry the bottom navigation (`202:4113`,
 * `202:471`, `202:963`): 84 high with a 44×44 radius-12 icon button on each
 * side and the gradient "X" mark centred.
 *
 * Distinct from `TopBar` (§5.1), which is the back-and-title bar used by the
 * pushed screens.
 *
 * Both buttons navigate on their own. They were props-only, and none of the
 * eighteen render sites passed one, so the two controls were inert on every
 * shell screen. Routing here rather than threading callbacks through eighteen
 * screens follows `BottomNav`, which already links directly.
 *
 * On the screen a button opens — the menu on «پروفایل من» — it replaces the
 * entry rather than pushing another copy of it, so back still leaves.
 *
 * **The bell is this app's, not the frames'.** None of them draws it; it opens
 * «اعلان‌ها» (`/notifications`) and sits beside the support button, on its
 * right, sized and styled like it. Its badge is the unread count: nothing at
 * zero, «۹+» above nine, in primary like the inbox's unread dot. The count is
 * read only with a session, and a failed read hides the badge. The count is
 * the notifications feature's, imported here for the same reason the buttons
 * route here: twenty screens render this bar and none passes it anything. The
 * three columns keep the X centred now that the left side holds two buttons.
 *
 * **The support button's dot** is an operator's reply that has not been read
 * in the chat, which counts only while the widget is loaded and closed. The
 * dot has no number, because the bell owns the count, and sits on the side
 * away from the bell, in its colours.
 */
interface AppTopBarProps {
  /** Overrides the default navigation — a screen that opens a sheet instead. */
  onOpenSupport?: () => void
  onOpenMenu?: () => void
}

function IconButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick?: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="relative flex size-[44px] items-center justify-center rounded-12 border border-line-8 bg-surface-5 text-ink"
    >
      {children}
    </button>
  )
}

export function AppTopBar({ onOpenSupport, onOpenMenu }: AppTopBarProps) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const go = (to: string) => () => void navigate(to, { replace: pathname === to })

  const count = useUnreadCount()
  const { unread } = useSupportChat()

  return (
    <header className="sx-col-app grid h-[84px] w-full shrink-0 grid-cols-[1fr_auto_1fr] items-center">
      <div className="justify-self-start">
        <IconButton label="منو" onClick={onOpenMenu ?? go('/settings')}>
          <Icon name="menu" size={20} />
        </IconButton>
      </div>

      <span
        className="bg-clip-text text-36 leading-[31px] font-bold text-transparent"
        style={{ backgroundImage: 'linear-gradient(180deg, #5b8def 0%, #2563eb 100%)' }}
        aria-label="ثمین اکس"
      >
        X
      </span>

      {/* RTL puts the bell first, so it lands to the support button's right. */}
      <div className="flex items-center gap-2 justify-self-end">
        <IconButton
          label={count > 0 ? `اعلان‌ها، ${toPersianDigits(String(count))} خوانده‌نشده` : 'اعلان‌ها'}
          onClick={go('/notifications')}
        >
          <Icon name="notification" size={20} />
          {count > 0 ? <UnreadBadge count={count} /> : null}
        </IconButton>

        <IconButton
          label={unread > 0 ? 'پشتیبانی، پیام خوانده‌نشده' : 'پشتیبانی'}
          onClick={onOpenSupport ?? go('/support')}
        >
          <Icon name="message" size={20} />
          {unread > 0 ? <UnreadDot /> : null}
        </IconButton>
      </div>
    </header>
  )
}

/** The support chat's unread reply: the bell's primary, on the far corner. */
function UnreadDot() {
  return (
    <span
      aria-hidden="true"
      className="absolute -top-1 -end-1 size-2.5 rounded-full bg-primary"
    />
  )
}

/**
 * The count on the bell's top corner, on the side away from the support
 * button. Read out by the button's own name, so hidden here. The figure is
 * set left to right, or the RTL line would print «۹+» as «+۹». White on
 * primary is about 5.1:1; on the danger red it would be 4.1, under AA for
 * text this small.
 */
function UnreadBadge({ count }: { count: number }) {
  return (
    <span
      aria-hidden="true"
      className="sx-tight absolute -top-1.5 -start-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-11 font-bold text-ink"
    >
      <span dir="ltr">{count > 9 ? '۹+' : toPersianDigits(String(count))}</span>
    </span>
  )
}
